import {
  Box,
  Stack,
  Paper,
  Typography,
  Chip,
  Button,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import { SmallTable } from '../components/SmallTable';
import SettingsIcon from '@mui/icons-material/Settings';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import PeopleIcon from '@mui/icons-material/People';
import ArrowIcon from '@mui/icons-material/ArrowRight';
import {
  MERGE_CASES,
  useMergeContext,
  STATE_MACHINE,
  MODES,
} from '../../../store/mergeContext';
import { useMemo, useState, useCallback } from 'react';
import { CRASH_ID_KEY } from '../utils';
import { useLocation } from 'react-router-dom';
import { t, Trans } from '@lingui/macro';
import { NewApiBaseUrl } from '../../../consts/new-api-base-url';
import useHttpApiNew from '../../../hooks/useHttpApiNew';
import SvgCustomIcon from '../../../components/icons/SvgCustomIcon';
import { collectMergeImages, BASIC_ORG, getOrgLabelByBasic } from '../merge-constants';
import {
  collectCarriedPeopleFromMatchDetails,
  formatPersonDisplayName,
  getPersonKey,
  resolvePeopleToMerge,
  buildMergeUnitSlots,
} from '../match-display-rows';
import {
  buildMatchSubmitBody,
  extractMatchApiErrorMessage,
} from '../merge-payload';
import { useMergedMergeConfiguration } from '../use-merged-merge-configuration';

const newPostUrl = new URL(`${NewApiBaseUrl}/match`);

const TABLE_CONFIG = [
  {
    category: 'Crash & Road',
    icon: SettingsIcon,
    count: 0,
    totalCount: 0,
  },
  {
    category: 'Units',
    icon: DirectionsCarFilledIcon,
    count: 0,
    totalCount: 0,
  },
  {
    category: 'People',
    icon: PeopleIcon,
    count: 0,
    totalCount: 0,
  },
];

export const MergePage = ({ fetchCrashes, onActionSuccess }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const {
    anchorDetail,
    hospitalDetail,
    insuranceDetail,
    policeDetail,
    crashByRole,
    dispatchMergeState,
    setCurrentState,
    mode,
    policePhotos,
    mergeCapabilities,
    peopleToMergeFromPairs,
    hasPairBasedMerge,
    confirmedMergePeople,
  } = useMergeContext();

  const myLocation = useLocation();
  const token = myLocation.state?.token || null;
  const init = myLocation.state?.init || {};
  const organizations = init?.organizations || [];
  const { mergedConfiguration } = useMergedMergeConfiguration(
    token,
    init?.configuration,
  );
  const configurationMap = useMemo(() => {
    const flatConfiguration = Object.values(mergedConfiguration ?? init?.configuration ?? {})
      ?.flatMap((conf) => conf?.elements?.flatMap((ele) => ele));
    return flatConfiguration?.reduce(
      (acc, val) => ({ ...acc, [val?.label]: { ...val } }),
      {},
    ) ?? {};
  }, [mergedConfiguration, init?.configuration]);

  const selectedCrashDetail = anchorDetail ?? {};
  const peopleToMerge = useMemo(() => {
    if (hasPairBasedMerge && peopleToMergeFromPairs.length > 0) {
      return peopleToMergeFromPairs
    }
    return resolvePeopleToMerge(
      selectedCrashDetail?.people,
      hospitalDetail,
      insuranceDetail,
    )
  }, [
    hasPairBasedMerge,
    peopleToMergeFromPairs,
    selectedCrashDetail?.people,
    hospitalDetail,
    insuranceDetail,
  ]);
  const carriedPeople = useMemo(
    () => collectCarriedPeopleFromMatchDetails([
      {
        detail: policeDetail,
        role: 'police',
        roleLabel: getOrgLabelByBasic(BASIC_ORG.POLICE, organizations, {
          police: t`Police`,
        }),
      },
      {
        detail: hospitalDetail,
        role: 'hospital',
        roleLabel: getOrgLabelByBasic(BASIC_ORG.HOSPITAL, organizations, {
          hospital: t`Hospital`,
        }),
      },
      {
        detail: insuranceDetail,
        role: 'insurance',
        roleLabel: getOrgLabelByBasic(BASIC_ORG.INSURANCE, organizations, {
          insurance: t`Insurance`,
        }),
      },
    ]),
    [policeDetail, hospitalDetail, insuranceDetail, organizations],
  );

  const unitSlots = useMemo(
    () => buildMergeUnitSlots(selectedCrashDetail, crashByRole),
    [selectedCrashDetail, crashByRole],
  );

  const dispatchNextPersonMerge = useCallback(() => {
    const undonePairIndex = peopleToMerge.findIndex((person) => !person?.done)
    if (undonePairIndex >= 0) {
      dispatchMergeState({
        type: MERGE_CASES.MERGE_PERSON,
        index: undonePairIndex,
      })
      return
    }

    const undoneCarried = carriedPeople.find((person) => !person?.done)
    if (undoneCarried?.carryRole) {
      dispatchMergeState({
        type: MERGE_CASES.MERGE_CARRIED_PERSON,
        person: undoneCarried,
        role: undoneCarried.carryRole,
      })
    }
  }, [peopleToMerge, carriedPeople, dispatchMergeState])

  const peopleValidationTotal = peopleToMerge.length + carriedPeople.length
  const peopleValidationDone =
    peopleToMerge.filter((person) => person?.done).length +
    carriedPeople.filter((person) => person?.done).length

  const mergeStatus = useMemo(
    () => {
      const unitsComplete =
        unitSlots.length === 0 || unitSlots.every((unit) => unit?.done)
      const peopleComplete =
        peopleToMerge.every((person) => person?.done) &&
        carriedPeople.every((person) => person?.done)
      return Boolean(
        selectedCrashDetail?.done &&
          unitsComplete &&
          peopleComplete,
      );
    },
    [selectedCrashDetail, unitSlots, peopleToMerge, carriedPeople],
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { request } = useHttpApiNew();

  const mergedTableConfig = TABLE_CONFIG?.map((c) => {
    if (c?.category === 'Crash & Road') {
      return {
        ...c,
        count: selectedCrashDetail?.done ? '1' : '0',
        totalCount: '1',
        action: () => dispatchMergeState({ type: MERGE_CASES.MERGE_ROAD }),
      };
    }
    if (c?.category === 'Units') {
      const doneUnits = unitSlots.filter((unit) => unit?.done).length;
      return {
        ...c,
        count: `${doneUnits}`,
        totalCount: `${unitSlots.length}`,
        caption: unitSlots.length === 0 ? (
          <Trans>No units to merge</Trans>
        ) : undefined,
        action:
          unitSlots.length > 0
            ? () =>
              dispatchMergeState({
                type: MERGE_CASES.MERGE_UNIT,
                index: unitSlots.findIndex((unit) => !unit.done),
              })
            : undefined,
      };
    }
    if (c?.category === 'People') {
      return {
        ...c,
        totalCount: `${peopleValidationTotal}`,
        count: `${peopleValidationDone}`,
        action: peopleValidationTotal > 0 ? dispatchNextPersonMerge : undefined,
      };
    }

    return c;
  });

  const sendValidateRequest = async () => {
    const crashIds = [
      policeDetail?.crash?.[CRASH_ID_KEY],
      hospitalDetail?.crash?.[CRASH_ID_KEY],
      insuranceDetail?.crash?.[CRASH_ID_KEY],
    ].filter(Boolean);
    const uniqueCrashIds = [...new Set(crashIds)];

    if (mode === MODES.MERGING && uniqueCrashIds.length < 2) {
      setSnackbar({
        open: true,
        message: t`Select records from at least two organizations to merge`,
        severity: 'error',
      });
      return;
    }

    if (mode === MODES.MERGING && !mergeCapabilities.hasPolice) {
      setSnackbar({
        open: true,
        message: t`A Police crash is required as merge master. Select a Police match before validating.`,
        severity: 'error',
      });
      return;
    }

    if (mode === MODES.VALIDATING && !mergeCapabilities.hasPolice) {
      setSnackbar({
        open: true,
        message: t`Select a Police record before validating`,
        severity: 'error',
      });
      return;
    }

    const reducedUrl = new URL(newPostUrl);

    try {
      const mergedImages = collectMergeImages({
        policePhotos,
        policeDetail,
        hospitalDetail,
        insuranceDetail,
      });
      const totalRawImages =
        (policePhotos?.length ?? 0) +
        (policeDetail?.crash?.images?.length ?? 0) +
        (hospitalDetail?.crash?.images?.length ?? 0) +
        (insuranceDetail?.crash?.images?.length ?? 0);

      if (totalRawImages > 0 && mergedImages.length === 0) {
        console.warn('[MergePage] Le fonti contenevano immagini ma nessuna e in formato base64 valido. Payload inviato senza immagini.');
      }

      const body = buildMatchSubmitBody({
        mode,
        policeDetail,
        anchorDetail,
        hospitalDetail,
        insuranceDetail,
        mergedImages,
        confirmedMergePeople,
        configurationMap,
        crashIdKey: CRASH_ID_KEY,
      });

      if (!body) {
        setSnackbar({
          open: true,
          message: t`A Police crash is required as merge master. Select a Police match before validating.`,
          severity: 'error',
        });
        return;
      }

      const res = await request(reducedUrl, 'POST', body, {
        Authorization: `Bearer ${token}`,
      });
      if (res.responseAnyError) {
        setSnackbar({
          open: true,
          message: extractMatchApiErrorMessage(res) || t`Error validating crash`,
          severity: 'error',
        });
        return;
      }
      await fetchCrashes();
      setCurrentState(STATE_MACHINE.CRASH_TABLE);
      onActionSuccess?.(
        mode === MODES.MERGING
          ? t`Merge completed successfully`
          : t`Crash validated successfully`,
      );
    } catch (err) {
      setSnackbar({
        open: true,
        message: t`Error validating crash: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  const pageTitle =
    mode === MODES.MERGING ? t`Merge status` : t`Validation status`;
  const actionStartLabel =
    mode === MODES.MERGING ? (
      <Trans>Start merging</Trans>
    ) : (
      <Trans>Start validation</Trans>
    );

  return (
    <Stack direction="column" gap={2.5} sx={{ maxWidth: 960, width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: 20,
              color: theme.palette.newColors?.gray || theme.palette.text.primary,
            }}
          >
            {pageTitle}
          </Typography>
          <Chip
            size="small"
            variant={mergeStatus ? 'filled' : 'outlined'}
            color={mergeStatus ? 'success' : 'warning'}
            label={
              mergeStatus ? <Trans>Complete</Trans> : <Trans>In progress</Trans>
            }
            sx={{ fontWeight: 600, height: 26 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          <Trans>Complete each section before validating the crash.</Trans>
        </Typography>
      </Paper>

      <Stack gap={1.5}>
        {mergedTableConfig?.map((config) => (
          <Box key={config.category}>
            <SmallTable
              category={config.category}
              count={config.count}
              actionLabel={
                config?.count !== config?.totalCount ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.25}
                  >
                    {actionStartLabel}
                    <ArrowIcon fontSize="small" />
                  </Box>
                ) : undefined
              }
              totalCount={config.totalCount}
              icon={config.icon}
              action={config?.action}
              caption={
                config.category === 'People' ? (
                  hasPairBasedMerge ? (
                    <Trans>One merge form per confirmed person pairing and per included person.</Trans>
                  ) : (
                    <Trans>Validate matched people and included persons before finishing.</Trans>
                  )
                ) : undefined
              }
            />
            {config.category === 'People' && hasPairBasedMerge && confirmedMergePeople.length > 0 ? (
              <Paper
                elevation={0}
                sx={{
                  mt: 1,
                  px: 2,
                  py: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '12px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}
                >
                  <Trans>Confirmed pairings</Trans>
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {confirmedMergePeople.map((entry) => (
                    <Typography key={getPersonKey(entry.anchorPerson)} variant="body2" fontWeight={600}>
                      {formatPersonDisplayName(entry.anchorPerson)}
                      {' → '}
                      {Object.entries(entry.matches).map(([role, person]) =>
                        `${role}: ${formatPersonDisplayName(person)}`,
                      ).join(' · ')}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            ) : null}
            {config.category === 'People' && carriedPeople.length > 0 ? (
              <Paper
                elevation={0}
                sx={{
                  mt: 1,
                  px: 2,
                  py: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '12px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}
                >
                  <Trans>Included without merge</Trans>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {carriedPeople.map((person) => (
                    <Chip
                      key={`${person.carryRole}-${person['Person id'] ?? formatPersonDisplayName(person)}`}
                      size="small"
                      variant="outlined"
                      color={person?.done ? 'success' : 'warning'}
                      label={`${formatPersonDisplayName(person)}${person.carryRoleLabel ? ` (${person.carryRoleLabel})` : ''}`}
                      onClick={() => {
                        if (!person?.carryRole) return
                        dispatchMergeState({
                          type: MERGE_CASES.MERGE_CARRIED_PERSON,
                          person,
                          role: person.carryRole,
                        })
                      }}
                      sx={{ fontWeight: 600, cursor: 'pointer' }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  <Trans>
                    Review and confirm each included person before validating the crash.
                  </Trans>
                </Typography>
              </Paper>
            ) : null}
          </Box>
        ))}
      </Stack>

      {mergeStatus ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 1,
          }}
        >
          <Button
            onClick={sendValidateRequest}
            variant="contained"
            sx={{
              background: theme.palette.newColors?.pinkGradient,
              color: 'white',
              display: 'flex',
              gap: 1,
              px: 3,
              py: 1.25,
              fontWeight: 700,
              borderRadius: '10px',
              boxShadow: theme.shadows[2],
              '&:hover': {
                background: theme.palette.newColors?.pinkGradient,
                opacity: 0.92,
              },
            }}
          >
            <SvgCustomIcon name="check" color="white" />
            <Trans>Validate crash</Trans>
          </Button>
        </Box>
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
